import React, { useState, useEffect, useCallback } from 'react';
import processingStepsService from '../../services/processingStepsService';
import ConfirmModal from './ConfirmModal';
import '../ConnectorForm.css';

// THE SETTINGS OF ONE PROCESSING STEP (processingstepsettings, modelled on marketplace settings) — their definitions:
// name, label, data type, default, required, shown. Read at runtime by the step's lambda; a task process whose lambda
// is a step's name prompts for them on Run Process (sync/db.js getProcessStepSettings).
//
// ONE EDITOR, TWO PLACES: Admin › Sync Processing Steps (the step's own page) and the process pane of a sync template
// task (AdminSyncTaskForm), where a Run Process lambda's settings are defined beside the process itself.
//
//   stepId  the processing_steps row
//   intro   optional text under the heading

const DATA_TYPES = ['TEXT', 'DATE', 'SELECT', 'PASSWORD', 'NUMBER', 'BOOLEAN', 'CHECKBOX'];
const YN = ['N', 'Y'];

const emptySetting = {
    settingName: '', settingLabel: '', type: 'CONFIG', dataType: 'TEXT',
    availableValues: '', settingValue: '', placeholder: '', helperText: '',
    required: 'N', displayYn: 'Y', locked: 'N', isInheritable: 'N',
    mustBeOverridden: 'N', sequence: '',
};

const errText = (err, fallback) => err.response?.data?.message || err.message || fallback;

export default function StepSettingsEditor({ stepId, intro = null }) {
    const id = stepId;
    const [settings, setSettings] = useState([]);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingForm, setSettingForm] = useState(null);   // null | setting object being added/edited
    const [settingEditId, setSettingEditId] = useState(null);
    const [settingSaving, setSettingSaving] = useState(false);
    const [settingError, setSettingError] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const loadSettings = useCallback(() => {
        if (!id) return;
        setSettingsLoading(true);
        processingStepsService.listSettings(id)
            .then(rows => setSettings(Array.isArray(rows) ? rows : []))
            .catch(err => setSettingError(errText(err, 'Failed to load settings')))
            .finally(() => setSettingsLoading(false));
    }, [id]);

    useEffect(() => { loadSettings(); }, [loadSettings]);

    const setSf = (field, value) => setSettingForm(f => ({ ...f, [field]: value }));

    const openNewSetting = () => { setSettingForm({ ...emptySetting }); setSettingEditId(null); setSettingError(null); };
    const openEditSetting = (s) => {
        setSettingForm({
            settingName: s.settingName || '', settingLabel: s.settingLabel || '',
            type: s.type || 'CONFIG', dataType: s.dataType || 'TEXT',
            availableValues: s.availableValues || '', settingValue: s.settingValue || '',
            placeholder: s.placeholder || '', helperText: s.helperText || '',
            required: s.required || 'N', displayYn: s.displayYn || 'Y', locked: s.locked || 'N',
            isInheritable: s.isInheritable || 'N', mustBeOverridden: s.mustBeOverridden || 'N',
            sequence: s.sequence != null ? s.sequence : '',
        });
        setSettingEditId(s.id);
        setSettingError(null);
    };

    const handleSettingSave = async () => {
        if (!settingForm.settingName.trim()) { setSettingError('Setting name is required'); return; }
        setSettingSaving(true);
        setSettingError(null);
        const payload = { ...settingForm, sequence: settingForm.sequence === '' ? null : Number(settingForm.sequence) };
        try {
            if (settingEditId === null) {
                await processingStepsService.createSetting(id, payload);
            } else {
                await processingStepsService.updateSetting(id, settingEditId, payload);
            }
            setSettingForm(null);
            setSettingEditId(null);
            loadSettings();
        } catch (err) {
            setSettingError(errText(err, 'Save failed'));
        } finally {
            setSettingSaving(false);
        }
    };

    const handleSettingDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await processingStepsService.removeSetting(id, deleteConfirm.id);
            setSettings(prev => prev.filter(s => s.id !== deleteConfirm.id));
        } catch (err) {
            setSettingError(errText(err, 'Delete failed'));
        } finally {
            setDeleteConfirm(null);
        }
    };

    return (
        <>
                <div className="form-section" style={{ marginTop: '1.5rem' }}>
                    <div className="subsection-header">
                        <h2 style={{ margin: 0 }}>Settings</h2>
                        <button type="button" className="btn-small btn-primary" onClick={openNewSetting}>+ Add Setting</button>
                    </div>
                    <p className="setting-value" style={{ marginTop: 0 }}>
                        {intro || 'Configurable settings for this step (modelled on marketplace settings). Read at runtime by the step\'s lambda.'}
                    </p>

                    {settingError && <div className="connector-form-error">{settingError}</div>}

                    {settingsLoading ? <p>Loading settings…</p> : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th><th>Label</th><th>Type</th><th>Data Type</th>
                                    <th>Default Value</th><th>Req</th><th>Show</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {settings.length === 0 ? (
                                    <tr><td colSpan={8} className="no-items">No settings yet.</td></tr>
                                ) : settings.map(s => (
                                    <tr key={s.id}>
                                        <td><code>{s.settingName}</code></td>
                                        <td>{s.settingLabel || '—'}</td>
                                        <td>{s.type || '—'}</td>
                                        <td>{s.dataType || '—'}</td>
                                        <td className="setting-value">{s.settingValue != null && s.settingValue !== '' ? s.settingValue : '—'}</td>
                                        <td>{s.required === 'Y' ? 'Y' : 'N'}</td>
                                        <td>{s.displayYn === 'N' ? 'N' : 'Y'}</td>
                                        <td>
                                            <button type="button" className="btn-small" onClick={() => openEditSetting(s)}>Edit</button>
                                            <button type="button" className="btn-small btn-danger" onClick={() => setDeleteConfirm({ id: s.id, name: s.settingName })}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {settingForm && (
                        <div className="connector-subsection inline-add-row" style={{ display: 'block', marginTop: '1rem' }}>
                            <h3 style={{ marginTop: 0 }}>{settingEditId === null ? 'New Setting' : `Edit “${settingForm.settingName || 'setting'}”`}</h3>
                            <div className="form-section">
                                <div className="form-group">
                                    <label>Setting Name <span className="required">*</span></label>
                                    <input className="form-control" value={settingForm.settingName}
                                        onChange={e => setSf('settingName', e.target.value.toUpperCase())} placeholder="e.g. RESERVATION_LOOKBACK_DAYS" />
                                </div>
                                <div className="form-group">
                                    <label>Label</label>
                                    <input className="form-control" value={settingForm.settingLabel}
                                        onChange={e => setSf('settingLabel', e.target.value)} placeholder="Human-friendly label" />
                                </div>
                                <div className="form-group">
                                    <label>Type</label>
                                    <input className="form-control" value={settingForm.type}
                                        onChange={e => setSf('type', e.target.value)} placeholder="e.g. CONFIG" />
                                </div>
                                <div className="form-group">
                                    <label>Data Type</label>
                                    <select className="form-control" value={settingForm.dataType} onChange={e => setSf('dataType', e.target.value)}>
                                        {DATA_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                {settingForm.dataType === 'SELECT' && (
                                    <div className="form-group">
                                        <label>Available Values</label>
                                        <input className="form-control" value={settingForm.availableValues}
                                            onChange={e => setSf('availableValues', e.target.value)} placeholder="Y:Yes|N:No" />
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Default Value</label>
                                    <textarea className="form-control" rows={2} value={settingForm.settingValue}
                                        onChange={e => setSf('settingValue', e.target.value)} placeholder="Default / template value" />
                                </div>
                                <div className="form-group">
                                    <label>Placeholder</label>
                                    <input className="form-control" value={settingForm.placeholder}
                                        onChange={e => setSf('placeholder', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label>Helper Text</label>
                                    <textarea className="form-control" rows={2} value={settingForm.helperText}
                                        onChange={e => setSf('helperText', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label>Sequence</label>
                                    <input className="form-control" type="number" value={settingForm.sequence}
                                        onChange={e => setSf('sequence', e.target.value)} />
                                </div>
                                {[
                                    ['required', 'Required'], ['displayYn', 'Display'], ['locked', 'Locked'],
                                    ['isInheritable', 'Inheritable'], ['mustBeOverridden', 'Must Be Overridden'],
                                ].map(([key, label]) => (
                                    <div className="form-group" key={key}>
                                        <label>{label}</label>
                                        <select className="form-control" value={settingForm[key]} onChange={e => setSf(key, e.target.value)}>
                                            {YN.map(v => <option key={v} value={v}>{v === 'Y' ? 'Yes' : 'No'}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={() => { setSettingForm(null); setSettingEditId(null); }} disabled={settingSaving}>
                                    Cancel
                                </button>
                                <button type="button" className="btn-primary" onClick={handleSettingSave} disabled={settingSaving || !settingForm.settingName.trim()}>
                                    {settingSaving ? 'Saving…' : (settingEditId === null ? 'Add Setting' : 'Save Setting')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            {deleteConfirm && (
                <ConfirmModal
                    title="Delete Setting"
                    message={`Delete setting ${deleteConfirm.name}?`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleSettingDelete}
                    onCancel={() => setDeleteConfirm(null)}
                />
            )}
        </>
    );
}

// THE SETTINGS OF A RUN PROCESS LAMBDA, by its name — on a sync template task's process pane. They live on the
// processing step of that name (the row Run Process reads them from), so they are SHARED by every task whose process
// runs the same lambda. A lambda with no step yet gets one on its first setting.
export function ProcessLambdaSettings({ lambdaName }) {
    const name = String(lambdaName || '').trim();
    const [stepId, setStepId] = useState(null);
    const [state, setState] = useState('loading');   // loading | none | ready | error
    const [error, setError] = useState(null);

    useEffect(() => {
        let live = true;
        if (!name) { setState('none'); return undefined; }
        setState('loading');
        processingStepsService.list()
            .then(rows => {
                if (!live) return;
                const step = (Array.isArray(rows) ? rows : []).find(r => r.name === name);
                setStepId(step ? step.id : null);
                setState(step ? 'ready' : 'none');
            })
            .catch(err => { if (live) { setError(errText(err, 'Failed to load settings')); setState('error'); } });
        return () => { live = false; };
    }, [name]);

    const create = async () => {
        setError(null);
        try {
            const r = await processingStepsService.create({ name, description: `Run Process lambda ${name} — its settings` });
            setStepId(r.id);
            setState('ready');
        } catch (err) {
            setError(errText(err, 'Could not create the settings'));
        }
    };

    const intro = `Settings for ${name} — asked for when this process is run, and read by the lambda. Shared by every `
        + 'task whose process runs this lambda.';
    if (!name) return null;
    if (state === 'loading') return <p className="setting-value">Loading settings…</p>;
    if (state === 'ready') return <StepSettingsEditor stepId={stepId} intro={intro} />;
    return (
        <div className="form-section" style={{ marginTop: '1.5rem' }}>
            <div className="subsection-header">
                <h2 style={{ margin: 0 }}>Settings</h2>
                <button type="button" className="btn-small btn-primary" onClick={create}>+ Add Setting</button>
            </div>
            <p className="setting-value" style={{ marginTop: 0 }}>{intro}</p>
            {error && <div className="connector-form-error">{error}</div>}
            <p className="no-items">No settings yet.</p>
        </div>
    );
}
